import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import type { AlertItem, AlertRoute } from '../../types';
import { CodeText } from '../Code';
import { SEVERITY_META as SEV } from '../severity';
import PredictionBadge from '../evolution/PredictionBadge';
import { C2_EVOLUTION_ENABLED } from '../../evolutionFlag';

// §15.2: turn a structured route into a frontend path. sub_tab → deep-link
// into the engine sub-tab with the alert + scenario as query params (the
// EngineShell reads them to open the named tab, pre-run, and banner the
// context). alert_detail → the dedicated resolution page.
export function routeToPath(route: AlertRoute): string {
  if (route.surface === 'alert_detail') return `/alert/${encodeURIComponent(route.alert_id)}`;
  const params = new URLSearchParams();
  if (route.tab) params.set('tab', route.tab);
  params.set('alert', route.alert_id);
  if (route.scenario) params.set('scenario', route.scenario);
  return `/engine/${route.engine ?? 1}?${params.toString()}`;
}

/**
 * AlertFeed · top band of the Engine Room (handoffV5 §12.1). Plain-language,
 * severity-ranked, clickable alerts built from existing breached tripwires +
 * open drift packets. Each alert routes to the thing that resolves it. Machine
 * codes are secondary tags, never the primary text.
 */
// Severity labels + colors come from the shared four-band scale (SEVERITY_META).
export default function AlertFeed() {
  const q = useQuery({ queryKey: ['alerts'], queryFn: api.alerts });
  const predictions = useQuery({ queryKey: ['evolution', 'predictions'], queryFn: api.evolutionPredictions, enabled: C2_EVOLUTION_ENABLED });
  const alerts: AlertItem[] = q.data ?? [];
  const sorted = [...alerts].sort((a, b) => (SEV[a.severity]?.rank ?? 9) - (SEV[b.severity]?.rank ?? 9));
  const breached = sorted.filter((a) => a.source !== 'signal' || a.severity === 'existential' || a.severity === 'high').length;

  if (q.isLoading || sorted.length === 0) {
    return (
      <div className="panel" style={{ marginBottom: 16, padding: '12px 18px' }}>
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <span className="dot" style={{ background: 'var(--olive)' }} />
          <span style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Alerts</span>
          <span className="small faint">{q.isLoading ? 'Loading…' : 'All clear · nothing breached'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="panel" style={{ marginBottom: 16, padding: '12px 18px', borderLeft: '3px solid var(--red)' }}>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <span className="pulse" />
          <span style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Alerts</span>
        </div>
        <span className="small">
          <b style={{ color: breached > 0 ? 'var(--red)' : 'var(--olive)', fontSize: 14 }}>{breached}</b>
          <span className="faint"> need attention · {sorted.length} tracked</span>
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {sorted.slice(0, 6).map((a) => {
          const sev = SEV[a.severity] ?? SEV.watch;
          const loud = a.severity === 'existential' || a.severity === 'high';
          const prediction = C2_EVOLUTION_ENABLED ? predictionForAlert(a, predictions.data ?? []) : null;
          return (
            <Link key={a.id} to={C2_EVOLUTION_ENABLED && a.id.includes('S-09') ? '/cascade' : routeToPath(a.route)} style={{ textDecoration: 'none' }}>
              <div
                className={`row alert-row ${loud ? 'loud' : ''} ${a.severity === 'existential' ? 'existential' : ''}`}
                style={{
                  justifyContent: 'space-between', alignItems: 'center', gap: 12,
                  padding: '10px 12px', borderRadius: 8, border: '1px solid var(--line)',
                }}
              >
                <div className="row" style={{ gap: 10, alignItems: 'center', minWidth: 0 }}>
                  <span className={`sev-chip ${a.severity}`}>
                    {a.severity === 'existential' && <span className="flash-dot" />}
                    {sev.label}
                  </span>
                  <span style={{ fontSize: 14, color: 'var(--ink)', fontWeight: loud ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.headline_plain}</span>
                  {prediction && <PredictionBadge prediction={prediction} />}
                </div>
                <div className="row" style={{ gap: 10, alignItems: 'center', flexShrink: 0 }}>
                  <span className="tiny faint"><CodeText text={a.machine_tag} compact /></span>
                  <span className="small" style={{ color: loud ? 'var(--red)' : 'var(--muted)', fontWeight: loud ? 700 : 500 }}>resolve →</span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function predictionForAlert(a: AlertItem, predictions: import('../../api/client').EvolutionPrediction[]) {
  if (a.id.includes('S-09')) return predictions.find((p) => p.prediction_type === 'type_2_structural_propagation') ?? null;
  if (a.route.engine === 3) return predictions.find((p) => p.engine_ref.engine_id === 3) ?? null;
  if (a.route.engine === 6) return predictions.find((p) => p.prediction_type === 'type_3_exogenous_early_warning') ?? null;
  return null;
}
